/**
 * UTM Tracking Utility
 * Helps track marketing campaigns and measure what's working
 */

/**
 * Add UTM parameters to a URL
 * @param {string} url - Base URL
 * @param {object} params - UTM parameters
 * @param {string} params.source - Traffic source (email, linkedin, twitter, etc.)
 * @param {string} params.medium - Marketing medium (cold_email, social_post, etc.)
 * @param {string} params.campaign - Campaign name/ID
 * @param {string} params.term - Optional keyword
 * @param {string} params.content - Optional content identifier
 * @returns {string} URL with UTM parameters
 */
export const addUTMParams = (url, { source, medium, campaign, term, content }) => {
  if (!url) return url;
  
  const urlObj = new URL(url);
  
  // Add UTM parameters
  if (source) urlObj.searchParams.set('utm_source', source);
  if (medium) urlObj.searchParams.set('utm_medium', medium);
  if (campaign) urlObj.searchParams.set('utm_campaign', campaign);
  if (term) urlObj.searchParams.set('utm_term', term);
  if (content) urlObj.searchParams.set('utm_content', content);
  
  return urlObj.toString();
};

/**
 * Get UTM parameters from current URL
 * Useful for tracking which campaign brought a user
 */
export const getUTMParams = () => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const utmParams = {};
  
  ['source', 'medium', 'campaign', 'term', 'content'].forEach(key => {
    const value = params.get(`utm_${key}`);
    if (value) utmParams[key] = value;
  });
  
  return utmParams;
};

/**
 * Track marketing event to backend
 * @param {string} event - Event type (page_view, signup, click, etc.)
 * @param {object} data - Event data
 */
export const trackMarketingEvent = async (event, data = {}) => {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';
    const utmParams = getUTMParams();
    
    await fetch(`${API_BASE_URL}/api/marketing/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event,
        ...utmParams,
        ...data,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        referrer: document.referrer,
      }),
    });
  } catch (error) {
    console.error('Failed to track marketing event:', error);
    // Don't throw - tracking failures shouldn't break the app
  }
};

/**
 * Preserve UTM parameters when navigating
 * Stores UTM params in sessionStorage so they persist across pages
 */
export const preserveUTMParams = () => {
  if (typeof window === 'undefined') return;
  
  const utmParams = getUTMParams();
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }
};

/**
 * Get preserved UTM parameters from sessionStorage
 */
export const getPreservedUTMParams = () => {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
};

/**
 * Common UTM presets for different marketing channels
 */
export const UTM_PRESETS = {
  coldEmail: (campaign) => ({
    source: 'email',
    medium: 'cold_email',
    campaign: campaign || 'cold_outreach',
  }),
  linkedin: (campaign) => ({
    source: 'linkedin',
    medium: 'social_post',
    campaign: campaign || 'linkedin_outreach',
  }),
  twitter: (campaign) => ({
    source: 'twitter',
    medium: 'social_post',
    campaign: campaign || 'twitter_outreach',
  }),
  blog: (campaign) => ({
    source: 'blog',
    medium: 'content',
    campaign: campaign || 'blog_post',
  }),
  referral: (referralCode) => ({
    source: 'referral',
    medium: 'referral',
    campaign: 'user_referral',
    term: referralCode,
  }),
};




